import React from "react";
import styled from "styled-components";
import { Spacing } from "@glossy";
import { PeriodRange } from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import Empty from "@ui-kit/empty";
import { tr } from "@radarly/i18n";
import { Clause } from "@radarly/models/clause";
import { FullFacet, getPanelFromFacet } from "@radarly/models/facets";
import { useIsCurrentInsightPageGlobal } from "@radarly/models/insight-pages/is";
import AutocompletePanel from "@radarly/shared/filterbar/panels/autocomplete";
import BiglistPanel from "@radarly/shared/filterbar/panels/biglist";
import BooleanPanel from "@radarly/shared/filterbar/panels/boolean";
import CheckboxPanel from "@radarly/shared/filterbar/panels/checkboxes";
import ChipsPanel from "@radarly/shared/filterbar/panels/chips";
import ClassificationPanel from "@radarly/shared/filterbar/panels/classification";
import MinMaxPanel from "@radarly/shared/filterbar/panels/min-max";
import MultipleListPanel from "@radarly/shared/filterbar/panels/multiple-list";
import QueriesBiglistPanel from "@radarly/shared/filterbar/panels/queries-big-list";
import SandboxedChipsPanel from "@radarly/shared/filterbar/panels/sandboxed-chips";
import SliderPanel from "@radarly/shared/filterbar/panels/slider";
import StringPanel from "@radarly/shared/filterbar/panels/string-single-value";
import TextPanel from "@radarly/shared/filterbar/panels/text";
import Search from "./search";

const EmptyPanel = styled(Empty)`
  width: 480px;
  padding: ${Spacing.Large};
`;

interface IProps {
  facet: FullFacet;
  clause: Clause;
  setClause: (clause: Clause) => void;
  onApply: () => void;
  locale?: Locale;
  hasTitle?: boolean;
  visible?: boolean;
  onCancel?: () => void;
  period: PeriodRange;
  validateLabel?: string;
  className?: string;
}

const RadarlyFilterbarPanels = ({
  onApply,
  setClause,
  clause,
  facet,
  locale = Locale.English,
  hasTitle,
  visible,
  onCancel,
  period,
  validateLabel,
  className,
}: IProps) => {
  const isGlobalInsightPage = useIsCurrentInsightPageGlobal();
  const panel = getPanelFromFacet(facet, isGlobalInsightPage);

  return panel === "search" ? (
    <Search
      clause={clause}
      setClause={setClause}
      hasTitle={hasTitle}
      facet={facet}
      onApply={onApply}
      visible={visible}
      onCancel={onCancel}
      validateLabel={validateLabel}
      className={className}
    />
  ) : panel === "checkboxes" ? (
    <CheckboxPanel
      clause={clause}
      setClause={setClause}
      facet={facet}
      hasTitle={hasTitle}
      onApply={onApply}
      onCancel={onCancel}
      period={period}
      validateLabel={validateLabel}
      className={className}
    />
  ) : panel === "multiple-list" ? (
    <MultipleListPanel
      clause={clause}
      setClause={setClause}
      facet={facet}
      hasTitle={hasTitle}
      onApply={onApply}
      onCancel={onCancel}
      validateLabel={validateLabel}
      className={className}
    />
  ) : panel === "chips" ? (
    <ChipsPanel
      clause={clause}
      setClause={setClause}
      facet={facet}
      hasTitle={hasTitle}
      onApply={onApply}
      onCancel={onCancel}
      validateLabel={validateLabel}
      className={className}
    />
  ) : panel === "sandboxed-chips" ? (
    <SandboxedChipsPanel
      clause={clause}
      setClause={setClause}
      facet={facet}
      hasTitle={hasTitle}
      onApply={onApply}
      onCancel={onCancel}
      validateLabel={validateLabel}
      className={className}
    />
  ) : panel === "slider" ? (
    <SliderPanel
      clause={clause}
      setClause={setClause}
      facet={facet}
      hasTitle={hasTitle}
      onApply={onApply}
      onCancel={onCancel}
      validateLabel={validateLabel}
      className={className}
    />
  ) : panel === "min-max" ? (
    <MinMaxPanel
      clause={clause}
      setClause={setClause}
      facet={facet}
      hasTitle={hasTitle}
      onApply={onApply}
      onCancel={onCancel}
      validateLabel={validateLabel}
      className={className}
    />
  ) : panel === "boolean" ? (
    <BooleanPanel
      clause={clause}
      setClause={setClause}
      facet={facet}
      hasTitle={hasTitle}
      onApply={onApply}
      onCancel={onCancel}
      validateLabel={validateLabel}
      className={className}
    />
  ) : panel === "autocomplete" ? (
    <AutocompletePanel
      clause={clause}
      setClause={setClause}
      facet={facet}
      locale={locale}
      hasTitle={hasTitle}
      onApply={onApply}
      onCancel={onCancel}
      visible={visible}
      validateLabel={validateLabel}
      className={className}
    />
  ) : panel === "big-list" ? (
    <BiglistPanel
      clause={clause}
      setClause={setClause}
      facet={facet}
      hasTitle={hasTitle}
      onApply={onApply}
      onCancel={onCancel}
      period={period}
      validateLabel={validateLabel}
      className={className}
    />
  ) : panel === "classification" ? (
    <ClassificationPanel
      clause={clause}
      setClause={setClause}
      facet={facet}
      hasTitle={hasTitle}
      onApply={onApply}
      onCancel={onCancel}
      period={period}
      validateLabel={validateLabel}
      className={className}
    />
  ) : panel === "text" ? (
    <TextPanel
      clause={clause}
      setClause={setClause}
      facet={facet}
      onApply={onApply}
      onCancel={onCancel}
      validateLabel={validateLabel}
      className={className}
    />
  ) : panel === "string-single-value" ? (
    <StringPanel
      clause={clause}
      setClause={setClause}
      facet={facet}
      onApply={onApply}
      onCancel={onCancel}
      validateLabel={validateLabel}
      className={className}
    />
  ) : panel === "queries-big-list" ? (
    <QueriesBiglistPanel
      clause={clause}
      setClause={setClause}
      facet={facet}
      hasTitle={hasTitle}
      onApply={onApply}
      onCancel={onCancel}
      period={period}
      validateLabel={validateLabel}
      className={className}
    />
  ) : (
    <EmptyPanel message={tr(locale, "FILTERBAR::PANEL::NODATA")} />
  );
};

export default RadarlyFilterbarPanels;
